/**
 * @description
 * Error context for the MediConnect Healthcare Information System
 * Provides global error handling and management throughout the application.
 * 
 * Key features:
 * - Global error state management
 * - Centralized error handling
 * - Error reporting and logging
 * - HIPAA-compliant error handling
 * 
 * @dependencies
 * - react: For context creation and hooks
 * - ../types/error-types: For healthcare error type definitions
 * - ../components/AlertSystem: For displaying error notifications
 */

import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { HealthcareApiError } from '../types/error-types';
import { showError, showHipaaAlert } from '../components/AlertSystem';
import { sanitizeError, createUserFriendlyError, sanitizedConsole } from '../utils/phi-sanitizer';

// Types for the error context
export interface ErrorContextState {
  error: Error | null;
  setError: (error: Error | null) => void;
  clearError: () => void;
  handleError: (error: Error, context?: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
}

// Create the error context
const ErrorContext = createContext<ErrorContextState | undefined>(undefined);

// Props for the error provider
export interface ErrorProviderProps {
  children: ReactNode;
}

/**
 * ErrorProvider component that provides global error handling
 */
export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle an error by setting it in the context and displaying a notification
   * CRITICAL: All errors are sanitized to prevent PHI exposure
   */
  const handleError = useCallback((error: Error, context?: string) => {
    // CRITICAL: Sanitize error before logging to prevent PHI exposure
    const sanitizedErrorMessage = sanitizeError(error);
    const sanitizedContext = context || 'application';

    // Log sanitized error to console
    sanitizedConsole.error(`Error in ${sanitizedContext}:`, sanitizedErrorMessage);

    // Set the error in the context
    setError(error);

    // Generate error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create user-friendly messages based on error type
    let userMessage = createUserFriendlyError(error, errorId);

    // Display an appropriate notification based on the error type
    if (error instanceof HealthcareApiError) {
      // For HIPAA-related errors, use a special notification
      if (error.isHipaaError()) {
        userMessage = 'A data security error occurred. Please contact support.';
        showHipaaAlert(userMessage);
        sanitizedConsole.warn('HIPAA violation detected (details redacted)');
      } else {
        // For other API errors, show a sanitized error notification
        const apiMessage = error.getUserFriendlyMessage();
        userMessage = sanitizeError(new Error(apiMessage));
        showError(userMessage);
      }

      // Log authentication errors for security monitoring
      if (error.isAuthError()) {
        sanitizedConsole.warn('Authentication error detected:', error.statusCode);
        // In a real app, we might trigger a session refresh or logout here
      }
    } else {
      // For standard errors, show a sanitized error notification
      userMessage = sanitizeError(error);
      showError(userMessage || 'An unexpected error occurred');
    }

    // In production, send sanitized error to monitoring service
    if (import.meta.env.PROD) {
      // CRITICAL: Sanitize error data before sending to external services
      const sanitizedErrorData = {
        errorId,
        context: sanitizedContext,
        message: sanitizedErrorMessage,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      // Example: Send sanitized error to monitoring service
      // errorReportingService.captureException(sanitizedErrorData);
      sanitizedConsole.log('Sanitized error prepared for error reporting service:', sanitizedErrorData);
    }
  }, []);

  // Create the context value
  const contextValue: ErrorContextState = {
    error,
    setError,
    clearError,
    handleError,
    isLoading,
    setIsLoading
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

/**
 * Custom hook to use the error context
 */
export const useError = (): ErrorContextState => {
  const context = useContext(ErrorContext);
  
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  
  return context;
};

export default ErrorProvider; 